import React, {FC, PropsWithChildren, useContext, useMemo} from 'react';
import {DefaultTheme, ThemeContext, ThemeProvider} from 'styled-components';

import {UserThemesEnum} from '../../../core/src/models/api/userThemesEnum';
import {lightTheme} from './light/lightTheme';
import {redesignedLightTheme} from './light/redesign/redesignedLightTheme';
import {useUserThemeStyles} from './themeHelpers';
import {UserTheme} from './themeTypes';

/*
 * Types.
 */

export interface ThemeContextType {
  theme: DefaultTheme;
}

export type ThemeContextReader = (props: ThemeContextType) => string;
export type ThemeReader = (theme: DefaultTheme) => string;

/*
 * Props.
 */

export interface UserThemeProviderProps {
  theme: UserThemesEnum;
  hasInboxRedesign: boolean;
  hasDropdownRedesign: boolean;
  hasP0AppSwitcher: boolean;
}

/*
 * Component.
 */

export const UserThemeProvider: FC<PropsWithChildren<UserThemeProviderProps>> = ({
  theme,
  hasInboxRedesign,
  hasDropdownRedesign,
  hasP0AppSwitcher,
  children,
}) => {
  const userTheme = useUserThemeStyles(
    hasInboxRedesign && theme === UserThemesEnum.DARK_SIDEBAR ? UserThemesEnum.LIGHT : theme,
    {hasInboxRedesign, hasDropdownRedesign, hasP0AppSwitcher},
  );
  return <ThemeProvider theme={userTheme}>{children}</ThemeProvider>;
};

export const LightUserThemeProvider: FC<PropsWithChildren<unknown>> = ({children}) => {
  const theme = useContext(ThemeContext);

  const resultTheme = useMemo<UserTheme>(
    () => ({
      ...(theme?.hasInboxRedesign ? redesignedLightTheme : lightTheme),
      hasDropdownRedesign: Boolean(theme?.hasDropdownRedesign),
      hasP0AppSwitcher: Boolean(theme?.hasP0AppSwitcher),
    }),
    [theme],
  );

  return <ThemeProvider theme={resultTheme}>{children}</ThemeProvider>;
};

/*
 * Hook.
 */

export function useUserTheme(): UserTheme {
  const theme = useContext(ThemeContext);
  return theme as UserTheme;
}
